import {
	ApplicationConfig,
	ErrorHandler,
	importProvidersFrom,
	inject,
	provideAppInitializer,
} from '@angular/core';
import { TranslateLoader, TranslateModule, TranslateService } from '@ngx-translate/core';
import {
	AppNavMenuPointsService,
	ChunkLoadingFailedErrorHandler,
	ENVIRONMENT,
	headersInterceptor,
	initializeTranslations,
	localeProvider,
	MENU_POINTS,
	MenuPointService,
	provideDatepickerTranslationsWithNgxTranslate,
	provideSelectTranslationsWithNgxTranslate,
	provideTableSort,
	TranslationsLoader,
	ThemeService,
	USER_FORMAT,
	provideFavicon,
} from '@agilox/common';
import {
	HttpClient,
	provideHttpClient,
	withInterceptors,
	withInterceptorsFromDi,
	withXhr,
} from '@angular/common/http';
import { BrowserModule } from '@angular/platform-browser';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule } from '@angular/material/sort';
import { NavModule, ToastService } from '@agilox/components';
import { IconModule, InputModule, TooltipModule } from '@agilox/ui';
import { MatPaginatorModule } from '@angular/material/paginator';
import { AUTH_CONFIG, AuthModule, MSALAUTHConfigFactory, userInitializationFactory } from '@auth';
import { environment } from '@selftest/env/environment';
import { DatePipe, DecimalPipe } from '@angular/common';
import { CustomDatePipe } from '@agilox/pipe/custom-date';
import { CustomNumberPipe, provideNumberFormatter } from '@agilox/pipe/custom-number';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideUserContext, UserStoreService } from '@user-store';
import { appRoutes } from './app.routes';
import { CustomTimePipe } from '@agilox/pipe/custom-time';
import { MsalService } from '@azure/msal-angular';
import { desktopMenupoints } from './general/desktop-menupoints';
import { mobileMenupoints } from './general/mobile-menupoints';
import { provideRouter, withHashLocation } from '@angular/router';

export const appConfig: ApplicationConfig = {
	providers: [
		provideFavicon(),
		ToastService,
		importProvidersFrom(
			TranslateModule.forRoot({
				loader: {
					provide: TranslateLoader,
					useClass: TranslationsLoader,
					deps: [HttpClient],
				},
			}),
			BrowserModule,
			MatTableModule,
			MatSortModule,
			NavModule,
			MatPaginatorModule,
			IconModule,
			TooltipModule,
			InputModule,
			AuthModule
		),
		provideHttpClient(
			withXhr(),
			withInterceptors([headersInterceptor]),
			withInterceptorsFromDi() // needed for MSAL
		),
		provideSelectTranslationsWithNgxTranslate(),
		provideTableSort(),
		provideDatepickerTranslationsWithNgxTranslate(),
		provideRouter(appRoutes, withHashLocation()),
		{
			provide: ErrorHandler,
			useClass: ChunkLoadingFailedErrorHandler,
		},
		UserStoreService,
		...provideUserContext(),
		provideAppInitializer(() => {
			const msalService = inject(MsalService);
			const userStore = inject(UserStoreService);
			return userInitializationFactory(msalService, userStore)();
		}),
		provideAppInitializer(() => {
			inject(ThemeService);
		}),
		{
			provide: USER_FORMAT,
			useFactory: (userStore: UserStoreService) => userStore.userFormat,
			deps: [UserStoreService],
		},
		{
			provide: AUTH_CONFIG,
			useFactory: MSALAUTHConfigFactory,
			deps: [ENVIRONMENT],
		},
		{
			provide: ENVIRONMENT,
			useValue: environment,
		},
		AppNavMenuPointsService,
		{
			provide: MENU_POINTS,
			useValue: {
				desktop: desktopMenupoints,
				mobile: mobileMenupoints,
			},
		},
		TranslateService,
		provideAppInitializer(() => {
			const initializerFn = initializeTranslations(inject(TranslateService));
			return initializerFn();
		}),
		MenuPointService,
		localeProvider,
		DatePipe,
		DecimalPipe,
		CustomDatePipe,
		CustomTimePipe,
		CustomNumberPipe,
		...provideNumberFormatter(),
		provideAnimations(),
	],
};
